import React, {
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SearchMobile from './SearchMobile';
import {MobileSearchActionsContext} from './MobileSearchContext';
import {SearchDispatchContext} from '../Context/ApplicationContextProvider';

function MobileSearchProvider({children, enabled}) {
  const [active, setActive] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const wasActiveRef = useRef(false);
  const dispatchSearch = useContext(SearchDispatchContext);

  const open = useCallback(() => setActive(true), []);
  const close = useCallback(() => setActive(false), []);
  const actions = useMemo(() => ({open, close}), [open, close]);

  useEffect(() => {
    if (!enabled && active) {
      setActive(false);
    }
  }, [active, enabled]);

  useEffect(() => {
    let actionFrameId = null;
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = active;

    const paintFrameId = requestAnimationFrame(() => {
      actionFrameId = requestAnimationFrame(() => {
        document.body.classList.toggle('stop-scrolling', active);

        if (!active && wasActive) {
          dispatchSearch({type: 'clear'});
        }
      });
    });

    return () => {
      cancelAnimationFrame(paintFrameId);
      if (actionFrameId !== null) cancelAnimationFrame(actionFrameId);
    };
  }, [active, dispatchSearch]);

  useLayoutEffect(() => {
    if (!active) return;

    const input = document.getElementById('h-mobile-search-input');
    if (input instanceof HTMLElement) {
      input.focus({preventScroll: true});
    }
  }, [active]);

  useEffect(() => () => {
    document.body.classList.remove('stop-scrolling');
  }, []);

  useEffect(() => {
    if (!active || contentReady) return;

    let contentFrameId = null;
    const paintFrameId = requestAnimationFrame(() => {
      contentFrameId = requestAnimationFrame(() => {
        startTransition(() => setContentReady(true));
      });
    });

    return () => {
      cancelAnimationFrame(paintFrameId);
      if (contentFrameId !== null) cancelAnimationFrame(contentFrameId);
    };
  }, [active, contentReady]);

  return (
    <MobileSearchActionsContext.Provider value={actions}>
      {children}
      {
        enabled && (
          <div
            aria-hidden={!active}
            className={`${active
              ? 'visible opacity-100 pointer-events-auto'
              : 'invisible opacity-0 pointer-events-none'} fixed inset-0 z-[1057] bg-white`}>
            <SearchMobile contentReady={contentReady}/>
          </div>
        )
      }
    </MobileSearchActionsContext.Provider>
  );
}

export default MobileSearchProvider;